import { useCallback, useEffect, useRef } from 'react'
import { shouldPromptGeofenceExit } from '@shared/sessions/geofence-exit'
import type { Session } from '@shared/sessions/types'
import {
  dismissActiveSessionGeofenceExit,
  markActiveSessionGeofenceExitPrompted,
  useActiveSession,
} from '../../../stores/active-session-store'
import {
  clearGeofenceExitNotification,
  showGeofenceExitNotification,
} from '../../../services/push-notification-service'
import type { UseCheckInOutResult } from './use-check-in-out'

export interface GeofenceExitPrompt {
  /** When the provider actually left the geofence (first fix outside), or null. */
  exitedAt: number | null
  /** Show the in-app "Looks like you've left — record your departure?" prompt. */
  visible: boolean
  /** Record the departure at `exitedAt` (server validates + splits the legs). */
  confirm: () => Promise<void>
  /** "I'm still here" — keep the session running; nothing asks again until re-entry. */
  dismiss: () => void
}

/**
 * Notifications are fired once per excursion (keyed by session + exitedAt) and
 * survive remounts within the same WebView life.
 */
const notifiedExcursions = new Set<string>()

/**
 * Shell-level side effect (lives on the always-mounted active-session banner):
 * fire the local "Looks like you've left" notification once per confirmed
 * excursion, and clear it when the provider comes back inside or answers.
 * Independent of which route is open — the in-session screen may be closed.
 */
export function useGeofenceExitNotification(): void {
  const snapshot = useActiveSession()
  const sessionUuid = snapshot?.sessionUuid ?? null
  const exitState = snapshot?.phase === 'inSession' ? snapshot.geofenceExit ?? null : null
  const exitedAt = exitState?.exitedAt ?? null
  const visible = shouldPromptGeofenceExit(exitState)
  const lastRef = useRef<{ sessionUuid: string; key: string } | null>(null)
  useEffect(() => {
    if (visible && sessionUuid && exitedAt != null) {
      const key = `${sessionUuid}:${exitedAt}`
      if (notifiedExcursions.has(key)) return
      notifiedExcursions.add(key)
      lastRef.current = { sessionUuid, key }
      void showGeofenceExitNotification({ sessionUuid, exitedAt })
      return
    }
    if (!visible && lastRef.current) {
      const { sessionUuid: uuid } = lastRef.current
      lastRef.current = null
      void clearGeofenceExitNotification(uuid)
    }
  }, [exitedAt, sessionUuid, visible])
}

/**
 * Geofence-exit departure prompt (owner decision 2026-10-08). The exit itself
 * is detected in the active-session store from the same GPS stream that
 * drives the km figure — this hook only surfaces it: in-app prompt state and
 * the two answers (the local notification is {@link useGeofenceExitNotification}
 * on the shell banner). Confirming records the departure with
 * `departedAt = exitedAt`, so the departure time is when they left, not when
 * they noticed — GPS after that moment goes to the departure leg server-side.
 *
 * Works with whatever position source feeds the store (foreground today,
 * native background later): no position sourcing here.
 */
export function useGeofenceExitPrompt({
  session,
  checkInFlow,
  enabled,
}: {
  session: Session
  checkInFlow: Pick<UseCheckInOutResult, 'checkOut' | 'busy'>
  /** Only while the session is checked in (the prompt is meaningless otherwise). */
  enabled: boolean
}): GeofenceExitPrompt {
  const snapshot = useActiveSession()
  const scoped = snapshot?.sessionUuid === session.uuid ? snapshot : null
  const exitState = scoped?.geofenceExit ?? null
  const exitedAt = enabled ? exitState?.exitedAt ?? null : null
  const visible = enabled && shouldPromptGeofenceExit(exitState)

  const confirm = useCallback(async () => {
    if (exitedAt == null || checkInFlow.busy) return
    const result = await checkInFlow.checkOut(session, { departedAt: exitedAt })
    if (result) {
      markActiveSessionGeofenceExitPrompted(session.uuid)
      void clearGeofenceExitNotification(session.uuid)
    }
  }, [checkInFlow, exitedAt, session])

  const dismiss = useCallback(() => {
    dismissActiveSessionGeofenceExit(session.uuid)
    void clearGeofenceExitNotification(session.uuid)
  }, [session.uuid])

  return { exitedAt, visible, confirm, dismiss }
}
